/**
 * Access management — `docs/SCREENS.md` §4.
 *
 * The pending list is where routing becomes visible, and the copy on each row is taken from the
 * server's own `because` strings rather than restated here. That is deliberate: a screen that
 * explains a rule in its own words is a second copy of the rule, and the two drift. The server
 * computes who must approve and why; this renders it.
 *
 * An out-of-tier request is marked before anybody approves it. Showing the consequence — 30-day
 * expiry, flagged for review — at the moment of the decision is the difference between a control
 * and a form.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import type { ApproverRole, IdentityKind } from "@hub/core/browser";
import { api, relative, remaining, useApi } from "../lib/api";
import { Card, KindBadge, KpiCard, ListItem, ParityStrip, Pill, TierBadge, Toast } from "../components";

interface Approver { role: ApproverRole; because: string; hasVeto: boolean }

interface RequestRow {
  id: string;
  action: string;
  resourceRef: string;
  justification: string;
  purposeRef: string | null;
  status: string;
  reviewFlag: boolean;
  createdAt: string;
  requiredApprovers: Approver[];
  approvals: Partial<Record<ApproverRole, "approve" | "deny">>;
  subject: { id: string; displayName: string; kind: IdentityKind; agentProfile: { capabilityTier: 1 | 2 | 3 } | null };
  requester: { id: string; displayName: string };
}

interface Elevation {
  id: string;
  expiresAt: string | null;
  purposeRef: string | null;
  identity: { id: string; displayName: string; kind: IdentityKind };
}

interface Bundle {
  id: string; name: string; forKind: IdentityKind; grants: string[];
  sensitiveDataClassIds: string[]; lastCertifiedAt: string | null;
  _count: { members: number };
}

export function Access() {
  const [toast, setToast] = useState<string | null>(null);
  const requests = useApi<{ requests: RequestRow[] }>("/v1/access/requests?status=pending");
  const elevations = useApi<{ elevations: Elevation[] }>("/v1/access/elevations");
  const bundles = useApi<{ bundles: Bundle[] }>("/v1/access/bundles");

  const pending = requests.data?.requests ?? [];
  const live = elevations.data?.elevations ?? [];
  const byKind = (k: IdentityKind, rows: { subject?: { kind: IdentityKind }; identity?: { kind: IdentityKind } }[]) =>
    rows.filter((r) => (r.subject?.kind ?? r.identity?.kind) === k).length;

  async function respond(r: RequestRow, role: ApproverRole, decision: "approve" | "deny") {
    try {
      const { outcome } = await api<{ outcome: { status: string; awaiting: ApproverRole[]; expiresAt: string | null; reviewFlag: boolean } }>(
        `/v1/access/requests/${r.id}/decide`,
        { method: "POST", body: JSON.stringify({ asRole: role, decision }) },
      );
      // The toast states the CONSEQUENCE, not just the click. "Approved" tells a reviewer nothing
      // about the 30-day expiry they just granted.
      setToast(
        outcome.status === "pending"
          ? `Recorded. Still awaiting ${outcome.awaiting.join(" and ")}.`
          : outcome.status === "denied"
            ? "Denied. The request is closed and the denial is in the chain."
            : outcome.reviewFlag
              ? "Approved out of tier — expires in 30 days and is flagged for the next review."
              : `Approved${outcome.expiresAt ? `, ${remaining(outcome.expiresAt)}` : ""}.`,
      );
      requests.reload();
      elevations.reload();
    } catch (e) {
      setToast(e instanceof Error ? e.message : "That did not work.");
    }
    setTimeout(() => setToast(null), 4200);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Access management</h1>
          <p className="sub">
            Who may reach what, decided by the same policy engine whichever kind is asking. The
            approvers on each request were computed from kind, tier and the sensitivity of the data —
            nobody chose them.
          </p>
        </div>
        <Link className="btn" to="/policies">Edit policies</Link>
      </div>

      <ParityStrip
        human={{ n: byKind("human", pending), label: "people waiting" }}
        agent={{ n: byKind("agent", pending), label: "agents waiting" }}
        principle="One policy decision · two credential mechanics"
      />

      <div className="grid k4" style={{ marginBottom: 14 }}>
        <KpiCard label="Pending requests" value={pending.length}
                 dual={{ human: byKind("human", pending), agent: byKind("agent", pending) }} />
        <KpiCard label="Active elevations" value={live.length}
                 dual={{ human: byKind("human", live), agent: byKind("agent", live) }}
                 delta="Every one of these ends by itself." />
        <KpiCard label="Standing privileged grants"
                 value={bundles.data?.bundles.filter((b) => b.sensitiveDataClassIds.length > 0).length ?? "…"}
                 delta="Bundles reaching sensitive or financial data." />
        <KpiCard label="Out of tier, awaiting review"
                 value={pending.filter((r) => r.reviewFlag).length}
                 delta="Approved out-of-tier grants expire in 30 days." />
      </div>

      <div className="stack">
        <Card title="Pending requests" link={<Link to="/graph">Trace in access graph</Link>}>
          {requests.error && <p className="err">{requests.error.message}</p>}
          {requests.loading && <p className="empty">Loading…</p>}
          {!requests.loading && pending.length === 0 && (
            <p className="empty">Nothing is waiting on a decision.</p>
          )}
          <div className="stack">
            {pending.map((r) => {
              const outstanding = r.requiredApprovers.filter((a) => r.approvals[a.role] === undefined);
              return (
                <div key={r.id} data-testid="pending-request">
                  <ListItem
                    title={
                      <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <KindBadge kind={r.subject.kind} label={r.subject.displayName} />
                        {r.subject.agentProfile && <TierBadge tier={r.subject.agentProfile.capabilityTier} />}
                        <span style={{ color: "var(--muted)" }}>wants to</span>
                        <strong>{r.action}</strong>
                        <span style={{ color: "var(--muted)" }}>{r.resourceRef}</span>
                        {r.reviewFlag && <Pill tone="warn">Outside its tier</Pill>}
                      </span>
                    }
                    detail={
                      <>
                        {r.justification}
                        {r.purposeRef && <> · purpose <code>{r.purposeRef}</code></>}
                        {" · "}requested by {r.requester.displayName} {relative(r.createdAt)}
                      </>
                    }
                  >
                    {outstanding.map((a) => (
                      <span key={a.role} style={{ display: "flex", gap: 6 }}>
                        <button className="btn small primary"
                                onClick={() => respond(r, a.role, "approve")}
                                data-testid={`approve-${a.role}`}>
                          Approve as {a.role.replace(/_/g, " ")}
                        </button>
                        <button className="btn small danger" onClick={() => respond(r, a.role, "deny")}>
                          Deny
                        </button>
                      </span>
                    ))}
                  </ListItem>
                  {/* The server's own words for why each approver is required. */}
                  <ul style={{ margin: "6px 0 0 14px", padding: 0, listStyle: "none" }}>
                    {r.requiredApprovers.map((a) => (
                      <li key={a.role} style={{ fontSize: 12, color: "var(--muted)", padding: "2px 0" }}>
                        <strong style={{ color: a.hasVeto ? "var(--warn)" : "var(--muted)" }}>
                          {a.role.replace(/_/g, " ")}
                          {a.hasVeto ? " (may refuse alone)" : ""}
                        </strong>
                        {" — "}{a.because}
                        {r.approvals[a.role] && <> · <Pill tone={r.approvals[a.role] === "approve" ? "ok" : "bad"}>{r.approvals[a.role]}</Pill></>}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </Card>

        <Card title="Active just-in-time elevations">
          {live.length === 0 && <p className="empty">No live elevations.</p>}
          <div className="stack">
            {live.slice(0, 12).map((e) => (
              <ListItem
                key={e.id}
                title={<KindBadge kind={e.identity.kind} label={e.identity.displayName} />}
                detail={e.purposeRef ? <>purpose <code>{e.purposeRef}</code></> : "no declared purpose"}
              >
                <Pill tone={
                  !e.expiresAt ? "bad"
                    : Date.parse(e.expiresAt) - Date.now() < 5 * 60_000 ? "warn" : "ok"
                }>
                  {remaining(e.expiresAt)}
                </Pill>
              </ListItem>
            ))}
          </div>
        </Card>

        <Card title="Entitlement bundles">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Bundle</th><th>For</th><th className="hide-m">Grants</th>
                  <th>Members</th><th className="hide-m">Sensitive data</th><th>Last certified</th>
                </tr>
              </thead>
              <tbody>
                {(bundles.data?.bundles ?? []).map((b) => (
                  <tr key={b.id}>
                    <td>{b.name}</td>
                    <td><KindBadge kind={b.forKind} /></td>
                    <td className="hide-m" style={{ color: "var(--muted)" }}>
                      {Array.isArray(b.grants) ? b.grants.join(", ") : "—"}
                    </td>
                    <td>{b._count.members}</td>
                    <td className="hide-m">
                      {b.sensitiveDataClassIds.length > 0
                        ? <Pill tone="warn">{b.sensitiveDataClassIds.length} class(es)</Pill>
                        : <span style={{ color: "var(--muted)" }}>none</span>}
                    </td>
                    <td>
                      {b.lastCertifiedAt
                        ? relative(b.lastCertifiedAt)
                        : <Pill tone="bad">never</Pill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {toast && <Toast message={toast} />}
    </>
  );
}
